import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { t } from 'i18next';
import { announce } from '../helper/announcer';
import '../styles/choralControlsDrawer.scss';
import Dropdown from './DropDown';
import ButtonComponent from './ButtonComponent';
import globalStore from '../store';
import RadioButton from './RadioButton';
var RADIO_DIRECTION_OPTS = [
    './assets/images/manipulative/numberChart/topRight.svg',
    './assets/images/manipulative/numberChart/leftDown.svg',
    './assets/images/manipulative/numberChart/bottomRight.svg',
    './assets/images/manipulative/numberChart/bottomUp.svg',
];
// Same translation keys used by NumberChartManipulativePopup so screen
// readers announce the direction identically in both UIs.
var RADIO_DIRECTION_ARIA_KEYS = [
    'text.dirTopRight',
    'text.dirLeftDown',
    'text.dirBottomRight',
    'text.dirBottomUp',
];
var ChoralControlsDrawer = function (_a) {
    var onClose = _a.onClose;
    var _b = useContext(DataContext), selectedTool = _b.selectedTool, manipulative = _b.manipulative;
    var SPEED_OPTS = [
        t('text.speedOptionSlow'),
        t('text.speedOptionNormal'),
        t('text.speedOptionFast'),
    ];
    var _c = useState(1), dDSelected = _c[0], setDDSected = _c[1];
    var _d = useState(0), dir = _d[0], setDir = _d[1];
    var _e = useState(false), isPlaying = _e[0], setIsPlaying = _e[1];
    var _f = useState(true), isInitialized = _f[0], setIsInitialized = _f[1]; // Next is available immediately; Play/speed/direction changes keep it true
    var _g = useState(false), isComplete = _g[0], setIsComplete = _g[1]; // Track if animation has completed (all cells revealed)
    var btnConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).btnConfig;
    var drawerRef = useRef(null);
    var onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    // Focus management + inert background + keyboard handler (SaveBtnWithPopup pattern)
    useEffect(function () {
        var el = drawerRef.current;
        if (!el)
            return;
        // Capture the element that had focus when the drawer opened so we can
        // restore focus to it when the drawer closes (standard modal pattern).
        var opener = document.activeElement;
        // Auto-focus first focusable element
        requestAnimationFrame(function () {
            var first = el.querySelector('button, input, [tabindex="0"]');
            first === null || first === void 0 ? void 0 : first.focus();
        });
        // Capture-phase keydown: Tab trap + Escape to close
        var handleKeyDown = function (e) {
            // Escape: skip if dropdown is open (let Dropdown handle first)
            if (e.key === 'Escape') {
                if (el.querySelector('.dropdownList'))
                    return;
                e.preventDefault();
                e.stopPropagation();
                onCloseRef.current();
                requestAnimationFrame(function () {
                    var _a;
                    // Prefer the opener, fall back to the right-panel Choral button.
                    var target = (_a = (opener && document.body.contains(opener) ? opener : null)) !== null && _a !== void 0 ? _a : document.querySelector('.choralBtnWrapper button');
                    target === null || target === void 0 ? void 0 : target.focus();
                });
                return;
            }
            // Tab: skip if dropdown is open (let Dropdown close itself)
            if (e.key === 'Tab') {
                if (el.querySelector('.dropdownList'))
                    return;
                e.preventDefault();
                var focusable = Array.from(el.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (f) {
                    return !f.closest('[inert]') &&
                        !f.closest('[aria-hidden="true"]') &&
                        !f.closest('.dropdownList');
                });
                if (!focusable.length)
                    return;
                var idx = focusable.indexOf(document.activeElement);
                if (e.shiftKey) {
                    focusable[idx <= 0 ? focusable.length - 1 : idx - 1].focus();
                }
                else {
                    focusable[idx >= focusable.length - 1 ? 0 : idx + 1].focus();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return function () {
            document.removeEventListener('keydown', handleKeyDown, true);
            // Close via any path (Escape, outside click, unmount) — restore focus
            // to whoever opened the drawer so JAWS/VoiceOver users don't get
            // dropped on the document body.
            requestAnimationFrame(function () {
                var _a;
                var target = (_a = (opener && document.body.contains(opener) ? opener : null)) !== null && _a !== void 0 ? _a : document.querySelector('.choralBtnWrapper button');
                target === null || target === void 0 ? void 0 : target.focus();
            });
        };
    }, []);
    // Initialize direction from selected number chart's saved choralDirectionIndex
    useEffect(function () {
        var _a;
        if (selectedTool.length > 0) {
            var selectedId_1 = selectedTool[0];
            var selectedManipulative = manipulative.find(function (m) { return m.data.id === selectedId_1; });
            if (selectedManipulative && selectedManipulative.type === 'numberCharts') {
                var chartData = selectedManipulative.data;
                var savedDirection = (_a = chartData.choralDirectionIndex) !== null && _a !== void 0 ? _a : 0;
                setDir(savedDirection);
            }
        }
    }, [selectedTool, manipulative]);
    // Listen for choral complete event
    useEffect(function () {
        var onChoralComplete = function (data) {
            if (data.id === 'choralComplete') {
                setIsPlaying(false);
                setIsComplete(true); // Mark animation as complete
            }
            else if (data.id === 'choralInterrupted') {
                // Something outside the choral flow (e.g. "Show or Hide All
                // Numbers") reset the reveal to scratch — resync play state, but
                // don't mark it complete: Next/Play should still be usable to
                // start a fresh reveal.
                setIsPlaying(false);
            }
        };
        customEventHandler.addEventListener('choralDrawer', onChoralComplete);
        return function () {
            customEventHandler.removeEventListener('choralDrawer', onChoralComplete);
            customEventHandler.dispatchEvent('choralDrawer', { id: 'choralReset' });
        };
    }, []);
    // if (!isOpen) {
    //   return null;
    // }
    // The popup is `position: fixed` (viewport-anchored) so its on-screen
    // location must be computed from the trigger BUTTON's bounding rect —
    // not the wrapper, which can be wider/taller than the button itself.
    // This pins the popup vertically centered on the button and just to
    // its left, matching the previous `position: absolute; right: 100%;
    // top: 50%` look.
    useLayoutEffect(function () {
        var _a, _b, _c;
        var el = drawerRef.current;
        if (!el)
            return;
        var wrapper = el.parentElement; // .stampEveryBtnWrapper
        if (!wrapper)
            return;
        // Find the trigger button — the first sibling of the popup that isn't
        // the popup itself. Avoids `querySelector('button')` which would also
        // match the OK button INSIDE the popup (depending on traversal order).
        var triggerSibling = Array.from(wrapper.children).find(function (c) { return c !== el; });
        var trigger = (_b = (_a = ((triggerSibling === null || triggerSibling === void 0 ? void 0 : triggerSibling.tagName) === 'BUTTON'
            ? triggerSibling
            : triggerSibling === null || triggerSibling === void 0 ? void 0 : triggerSibling.querySelector('button'))) !== null && _a !== void 0 ? _a : triggerSibling) !== null && _b !== void 0 ? _b : wrapper;
        var wrapperRef = ((_c = document.getElementById('choralDrawerID')) === null || _c === void 0 ? void 0 : _c.offsetHeight) || 0;
        var updatePosition = function () {
            var rect = trigger.getBoundingClientRect();
            el.style.top = "".concat(rect.top - wrapperRef / 2 - rect.height / 2, "px");
            el.style.right = "".concat(window.innerWidth - rect.left, "px");
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return function () {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, []);
    return (_jsxs("div", { ref: drawerRef, className: "choralDrawer", id: "choralDrawerID", role: "dialog", "aria-label": t('text.choralCounting'), children: [_jsxs("div", { className: "choralWrapper", children: [_jsxs("div", { className: "choralControlBtnContainer", children: [_jsx(ButtonComponent, { imgSrc: isPlaying ? btnConfig.pause.imgSrc : btnConfig.choral.imgSrc, defaultCSS: "choralBtn", normalCSS: "", type: "imageType1", clickHandler: function () {
                                    if (isComplete) {
                                        setIsComplete(false); // Reset completion state to allow replay
                                    }
                                    setIsPlaying(function (prev) { return !prev; });
                                    setIsInitialized(true); // Mark as initialized when play starts
                                    var nextAction = isPlaying ? 'choralPause' : 'choralPlay';
                                    customEventHandler.dispatchEvent('choralDrawer', {
                                        id: nextAction,
                                        val: {
                                            direction: dir,
                                            speed: dDSelected,
                                        },
                                    });
                                    announce(t(nextAction === 'choralPlay' ? 'text.choralPlaying' : 'text.choralPaused'));
                                }, title: isPlaying ? t('text.pause') : t('text.play'), ariaLabel: isPlaying ? 'text.pause' : 'text.play', showToolTip: true, direction: "bottom" }), _jsx(ButtonComponent, { imgSrc: btnConfig.next.imgSrc, defaultCSS: "choralBtn", normalCSS: "", type: "imageType1", clickHandler: function () {
                                    if (!isInitialized || isPlaying || isComplete)
                                        return; // Prevent action when disabled
                                    setIsInitialized(true); // Mark as initialized when next is clicked
                                    customEventHandler.dispatchEvent('choralDrawer', {
                                        id: 'choralNext',
                                        val: {
                                            direction: dir,
                                            speed: dDSelected,
                                        },
                                    });
                                    announce(t('text.choralNextCell'));
                                }, disabled: isComplete || !isInitialized || isPlaying, tabIndex: isComplete || !isInitialized || isPlaying ? -1 : 0, showToolTip: true, title: "text.next", direction: "bottom" })] }), _jsxs("div", { className: " choralSpeedSection", children: [_jsx("label", { className: "labelText", children: t('text.playSpeed') }), _jsx(Dropdown, { optionWidthType: "full", id: "number-type-selection-dropDown", className: "dropdownField1", options: SPEED_OPTS, value: SPEED_OPTS[dDSelected], onChange: function (dropdownId, data) {
                                    setDDSected(data.index);
                                    setIsPlaying(false);
                                    setIsInitialized(true); // Mark as initialized when speed changes (resets cells)
                                    setIsComplete(false); // Reset completion state when speed changes
                                    customEventHandler.dispatchEvent('choralDrawer', {
                                        id: 'choralSpeed',
                                        val: {
                                            direction: dir,
                                            speed: data.index,
                                        },
                                    });
                                    announce(t('text.choralSpeedChanged'));
                                } })] })] }), _jsxs("div", { className: "bottomContainer upperContainer", children: [_jsx("div", { style: { position: 'absolute' } }), _jsx("div", { className: "header", "aria-hidden": "true", children: t('text.direction') }), _jsxs("fieldset", { className: "radioFieldsetDirection", children: [_jsx("legend", { className: "sronly", children: t('text.direction') }), _jsx(RadioButton, { id: "direction", options: RADIO_DIRECTION_OPTS, ariaLabels: RADIO_DIRECTION_ARIA_KEYS.map(function (k) { return t(k); }), useAriaLabelFromOutside: true, selected: dir, onChange: function (value) {
                                    setDir(value);
                                    setIsPlaying(false); // Reset play state when direction changes
                                    setIsInitialized(true); // Mark as initialized when direction changes (resets cells)
                                    setIsComplete(false); // Reset completion state when direction changes
                                    customEventHandler.dispatchEvent('choralDrawer', {
                                        id: 'choralDirection',
                                        val: {
                                            direction: value,
                                            speed: dDSelected,
                                        },
                                    });
                                    announce(t('text.choralDirectionChanged'));
                                }, className: "radioGap", optionsType: "image" })] })] })] }));
};
export default ChoralControlsDrawer;
